import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Info } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/page';
import { AccessSettingsFields, type AccessSettingsValue } from '@/components/products/AccessFields';
import {
  ACCESS_MODELS,
  AccessTag,
  AppMark,
  CardHead,
  FieldError,
  KV,
  ROLE_NAME_RE,
  SLUG_RE,
  slugify,
  validUrl,
} from '@/components/products/shared';
import { cn } from '@/lib/utils';
import { useCreateProduct, useOrganizations, useProducts, useToast } from '@/hooks';

const STEPS = ['Basics', 'Access model', 'Review'] as const;
const NO_ORG = '__none__';

export function ProductNewPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createProduct = useCreateProduct();
  const { data: orgs = [] } = useOrganizations();
  const { data: existing = [] } = useProducts();

  const [step, setStep] = useState(1);
  const [org, setOrg] = useState(NO_ORG);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [access, setAccess] = useState<AccessSettingsValue>({
    access_model: 'gated',
    default_role: '',
    member_org_id: '',
    resource: '',
    include_permissions_in_token: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  const activeOrgs = orgs.filter((o) => o.is_active);
  const orgName = activeOrgs.find((o) => o.id === org)?.name;

  const check = {
    name: () => (name.trim() ? '' : 'Enter a name for the app.'),
    slug: () => {
      const v = slug.trim();
      if (!v) return 'Enter a slug.';
      if (!SLUG_RE.test(v)) return 'Use lowercase letters, numbers and single hyphens — no spaces.';
      if (existing.some((p) => p.slug === v)) return `“${v}” is taken. Try another slug.`;
      return '';
    },
    member_org_id: () =>
      access.access_model === 'org_members' && !access.member_org_id
        ? 'Choose the organization whose members can sign in.'
        : '',
    resource: () =>
      validUrl(access.resource.trim()) ? '' : 'Enter an absolute URL, like https://api.example.com',
    default_role: () =>
      !access.default_role.trim() || ROLE_NAME_RE.test(access.default_role.trim())
        ? ''
        : 'Use lowercase letters, numbers, hyphens or underscores.',
  };

  const validate = (keys: (keyof typeof check)[]) => {
    const next: Record<string, string> = {};
    for (const k of keys) {
      const msg = check[k]();
      if (msg) next[k] = msg;
    }
    setErrors((prev) => {
      const merged = { ...prev };
      for (const k of keys) delete merged[k];
      return { ...merged, ...next };
    });
    return Object.keys(next).length === 0;
  };

  const goto = (n: number) => {
    setSubmitError('');
    setStep(n);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const next = () => {
    const ok = step === 1 ? validate(['name', 'slug']) : validate(['member_org_id', 'resource', 'default_role']);
    if (ok) goto(step + 1);
  };

  const handleCreate = async () => {
    if (!validate(['name', 'slug'])) return goto(1);
    if (!validate(['member_org_id', 'resource', 'default_role'])) return goto(2);
    setSubmitError('');
    try {
      const product = await createProduct.mutateAsync({
        organization_id: org === NO_ORG ? undefined : org,
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim(),
        settings: {
          access_model: access.access_model,
          resource: access.resource.trim() || undefined,
          default_role:
            access.access_model === 'org_members' ? access.default_role.trim() : undefined,
          member_org_id:
            access.access_model === 'org_members' ? access.member_org_id : undefined,
          include_permissions_in_token: access.include_permissions_in_token,
        },
      });
      toast({ title: 'App created', description: `${product.name} is ready. Add roles next.` });
      navigate(`/products/${product.id}?tab=roles`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create the app');
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            <Link to="/products" className="text-primary hover:underline">
              Apps
            </Link>
            <span>/</span>
            <span>New</span>
          </>
        }
        title="New app"
        description="An app is a resource people sign in to. After creating it, add roles, grant access and register an OAuth client."
      />

      <div className="grid items-start gap-6 lg:grid-cols-[200px_minmax(0,1fr)]">
        <nav
          aria-label="Steps"
          className="flex gap-0.5 overflow-x-auto lg:sticky lg:top-20 lg:flex-col"
        >
          {STEPS.map((label, i) => {
            const n = i + 1;
            const on = n === step;
            const done = n < step;
            return (
              <button
                key={label}
                type="button"
                disabled={n > step}
                aria-current={on ? 'step' : undefined}
                onClick={() => n < step && goto(n)}
                className={cn(
                  'flex w-auto flex-none items-center gap-2.5 rounded-[7px] px-2.5 py-2 text-left font-medium text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground disabled:pointer-events-none lg:w-full',
                  on && 'bg-foreground/[0.06] text-foreground'
                )}
              >
                <span
                  className={cn(
                    'grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full border border-border font-mono text-[11px]',
                    on && 'border-foreground bg-foreground text-background',
                    done && 'border-success/40 text-success'
                  )}
                >
                  {done ? '✓' : n}
                </span>
                {label}
              </button>
            );
          })}
        </nav>

        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (step < 3) next();
            else handleCreate();
          }}
        >
          {step === 1 && (
            <Card>
              <CardHead
                title="Basics"
                sub={
                  <>
                    The slug becomes the key for this app in the{' '}
                    <code className="text-xs">products</code> token claim.
                  </>
                }
              />
              <div className="grid gap-4 p-[18px] sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="f-org">
                    Organization
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>
                  </Label>
                  <Select value={org} onValueChange={setOrg}>
                    <SelectTrigger id="f-org">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_ORG}>No organization</SelectItem>
                      {activeOrgs.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Apps aren’t owned by an organization. Pick one only for organization-member
                    access.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="f-name">Name</Label>
                  <Input
                    id="f-name"
                    required
                    maxLength={80}
                    placeholder="Billing dashboard"
                    value={name}
                    aria-invalid={!!errors.name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (!slugTouched) setSlug(slugify(e.target.value).slice(0, 48));
                    }}
                    onBlur={() => {
                      validate(['name']);
                      if (slug) validate(['slug']);
                    }}
                    autoFocus
                  />
                  <FieldError>{errors.name}</FieldError>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="f-slug">Slug</Label>
                  <Input
                    id="f-slug"
                    required
                    maxLength={48}
                    className="font-mono"
                    placeholder="billing-dashboard"
                    spellCheck={false}
                    autoCapitalize="off"
                    value={slug}
                    aria-invalid={!!errors.slug}
                    onChange={(e) => {
                      setSlug(e.target.value);
                      setSlugTouched(e.target.value.length > 0);
                    }}
                    onBlur={() => {
                      setSlug((s) => slugify(s).slice(0, 48));
                      window.setTimeout(() => validate(['slug']), 0);
                    }}
                  />
                  <FieldError>{errors.slug}</FieldError>
                  <p className="text-xs text-muted-foreground">
                    Generated from the name. Lowercase letters, numbers and hyphens.
                  </p>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="f-desc">
                    Description
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">optional</span>
                  </Label>
                  <Textarea
                    id="f-desc"
                    maxLength={280}
                    placeholder="What people use this app for"
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setDescription(e.target.value)
                    }
                  />
                </div>
              </div>
              <Foot>
                <Button type="button" variant="outline" asChild>
                  <Link to="/products">Cancel</Link>
                </Button>
                <Button type="submit">
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Foot>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <CardHead title="Access model" sub="Decides who can sign in. You can change it later." />
              <div className="p-[18px]">
                <AccessSettingsFields
                  value={access}
                  onChange={(v) =>
                    setAccess(
                      v.access_model === 'org_members' && !v.member_org_id && org !== NO_ORG
                        ? { ...v, member_org_id: org }
                        : v
                    )
                  }
                  orgs={activeOrgs}
                  errors={errors}
                />
              </div>
              <Foot>
                <Button type="button" variant="outline" onClick={() => goto(1)}>
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <Button type="submit">
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Foot>
            </Card>
          )}

          {step === 3 && (
            <Card>
              <CardHead title="Review" sub="Check the details before creating the app." />
              <div className="space-y-5 p-[18px]">
                <div className="flex items-center gap-3">
                  <AppMark name={name} size="lg" />
                  <span className="min-w-0">
                    <b className="block truncate font-semibold">{name.trim()}</b>
                    <span className="block truncate font-mono text-xs text-muted-foreground">
                      {slug}
                    </span>
                  </span>
                </div>
                <KV
                  rows={[
                    {
                      label: 'Organization',
                      value: orgName ?? <span className="text-muted-foreground">None</span>,
                    },
                    ...(access.access_model === 'org_members'
                      ? [
                          {
                            label: 'Member organization',
                            value:
                              activeOrgs.find((o) => o.id === access.member_org_id)?.name ?? '—',
                          },
                        ]
                      : []),
                    { label: 'Slug', value: <code className="text-xs">{slug}</code> },
                    {
                      label: 'Description',
                      value: description.trim() || (
                        <span className="text-muted-foreground">None</span>
                      ),
                    },
                    {
                      label: 'Access model',
                      value: (
                        <div className="space-y-1">
                          <AccessTag model={access.access_model} />
                          <p className="text-xs text-muted-foreground">
                            {ACCESS_MODELS[access.access_model].desc}
                          </p>
                        </div>
                      ),
                    },
                    ...(access.access_model === 'org_members'
                      ? [
                          {
                            label: 'Default role',
                            value: access.default_role.trim() ? (
                              <code className="text-xs">{access.default_role.trim()}</code>
                            ) : (
                              <span className="text-muted-foreground">None</span>
                            ),
                          },
                        ]
                      : []),
                    {
                      label: 'Resource audience',
                      value: access.resource.trim() ? (
                        <code className="break-all text-xs">{access.resource.trim()}</code>
                      ) : (
                        <span className="text-muted-foreground">Not set</span>
                      ),
                    },
                    {
                      label: 'Permissions in tokens',
                      value: access.include_permissions_in_token ? 'Included' : 'Not included',
                    },
                  ]}
                />
                <div className="flex gap-3 rounded-lg border border-border bg-foreground/[0.03] p-3.5">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <b className="text-[13.5px] font-semibold">Next steps</b>
                    <p className="text-[13px] text-muted-foreground">
                      Define roles, grant access to the first people, then create an OAuth client
                      for each application that signs users in.
                    </p>
                  </div>
                </div>
                {submitError && (
                  <p
                    role="alert"
                    className="rounded-lg border border-destructive/40 bg-destructive/[0.08] px-3.5 py-2.5 text-[13px] text-destructive"
                  >
                    {submitError}
                  </p>
                )}
              </div>
              <Foot>
                <Button type="button" variant="outline" onClick={() => goto(2)}>
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <Button type="submit" disabled={createProduct.isPending}>
                  {createProduct.isPending ? 'Creating…' : 'Create app'}
                </Button>
              </Foot>
            </Card>
          )}
        </form>
      </div>
    </div>
  );
}

function Foot({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col-reverse justify-between gap-3 border-t border-border px-[18px] py-3.5 sm:flex-row">
      {children}
    </div>
  );
}
